import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { xadmApi } from '../../api/xadm.js';
import { Spinner, ErrorBox, EmptyBox } from '../../components/ui.js';
import { LineChart } from '../../components/LineChart.js';
import { useUrlUpdate } from '../../lib/hooks.js';
import { num } from '../../lib/format.js';

/**
 * 매출(월별) — 원본 /home/bananapartner/xadm/sale_month_list.php.
 *
 * 일반 주문과 랜덤박스 주문을 합산하는 기존 집계 기준은 유지하고, 연간 흐름과
 * 월별 성과를 먼저 파악한 뒤 오른쪽 패널에서 일별 상세를 탐색하도록 구성한다.
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function currentYear() {
  return String(new Date().getFullYear());
}

function weekdayOf(date: string) {
  return new Date(`${date}T00:00:00`).getDay();
}

function signed(value: number) {
  return `${value > 0 ? '+' : ''}${num(value)}`;
}

export function SalesPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const year = params.get('year') ?? currentYear();
  const [hoverMonth, setHoverMonth] = useState<string | null>(null);
  const [pinnedMonth, setPinnedMonth] = useState<string | null>(null);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openMonth = hoverMonth ?? pinnedMonth;

  const years = useQuery({
    queryKey: ['xadm-sales-years'],
    queryFn: () => xadmApi.salesYears(),
  });

  const monthly = useQuery({
    queryKey: ['xadm-sales-monthly', year],
    queryFn: () => xadmApi.salesMonthly(year),
  });

  const daily = useQuery({
    queryKey: ['xadm-sales-daily', openMonth],
    queryFn: () => xadmApi.salesDaily(openMonth!),
    enabled: openMonth !== null,
  });

  const openMonthRow = monthly.data?.months.find((month) => month.month === openMonth) ?? null;
  const summary = useMemo(() => {
    const rows = monthly.data?.months ?? [];
    const latest = rows[0] ?? null;
    const previous = rows[1] ?? null;
    const trend = [...rows].reverse();
    const totalSales = rows.reduce((sum, row) => sum + row.sales, 0);
    const totalOrders = rows.reduce((sum, row) => sum + row.orderCount, 0);
    const delta = latest && previous ? latest.sales - previous.sales : null;
    return {
      monthCount: rows.length,
      latest,
      delta,
      deltaRate: delta === null || !previous?.sales ? null : (delta / previous.sales) * 100,
      totalSales,
      totalOrders,
      averageMonthlySales: rows.length ? Math.round(totalSales / rows.length) : 0,
      averageOrderValue: totalOrders ? Math.round(totalSales / totalOrders) : 0,
      peak: rows.length === 0 ? null : rows.reduce((max, row) => row.sales > max.sales ? row : max),
      trend,
    };
  }, [monthly.data]);

  const trendLabels = summary.trend.map((month) => month.month);
  const trendSeries = [
    { id: 'sales', label: '매출', points: summary.trend.map((month) => ({ x: month.month, y: month.sales })) },
  ];

  const cancelHoverClose = () => {
    if (hoverCloseTimer.current !== null) clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = null;
  };

  const previewMonth = (month: string) => {
    cancelHoverClose();
    setHoverMonth(month);
  };

  const schedulePreviewClose = () => {
    cancelHoverClose();
    hoverCloseTimer.current = setTimeout(() => setHoverMonth(null), 180);
  };

  const togglePinnedMonth = (month: string) => {
    cancelHoverClose();
    setHoverMonth(null);
    setPinnedMonth((current) => current === month ? null : month);
  };

  const selectYear = (selectedYear: string) => {
    setHoverMonth(null);
    setPinnedMonth(null);
    update({ year: selectedYear });
  };

  useEffect(() => () => cancelHoverClose(), []);

  return (
    <section className="visit-page sales-page">
      <header className="page-head visit-page__head">
        <div>
          <h1 className="page-title">매출 통계</h1>
          <p className="page-desc">연간 매출 흐름을 비교하고 월별·일별 주문 성과를 빠르게 탐색합니다.</p>
        </div>
        <span className="visit-page__source"><i aria-hidden="true" /> MONTHLY SALES LOG</span>
      </header>

      <nav className="visit-weekdays sales-years" aria-label="조회 연도">
        <span className="visit-weekdays__label">연도 선택</span>
        {years.isLoading ? (
          <Spinner />
        ) : years.isError ? (
          <ErrorBox error={years.error} />
        ) : (
          <div className="visit-weekdays__buttons sales-years__buttons">
            {(years.data?.years ?? []).map((item) => (
              <button
                key={item}
                type="button"
                className={item === year ? 'visit-weekday visit-weekday--active' : 'visit-weekday'}
                aria-pressed={item === year}
                onClick={() => selectYear(item)}
              >
                {item}
              </button>
            ))}
          </div>
        )}
        <p>일반 주문과 랜덤박스 주문을 합산한 결과입니다.</p>
      </nav>

      {!monthly.isLoading && !monthly.isError && summary.monthCount > 0 && (
        <>
          <div className="visit-summary" aria-label="매출 핵심 지표">
            <article className="visit-summary__item visit-summary__item--primary sales-summary__total">
              <span>{year}년 누적 매출</span>
              <strong>{num(summary.totalSales)}</strong>
              <small>{summary.monthCount}개월 집계 · 월평균 {num(summary.averageMonthlySales)}</small>
            </article>
            <article className={`visit-summary__item visit-summary__item--delta ${summary.delta !== null && summary.delta < 0 ? 'visit-summary__item--down' : ''}`}>
              <span>최근월 전월 대비</span>
              <strong>{summary.delta === null ? '—' : signed(summary.delta)}</strong>
              <small>{summary.deltaRate === null ? '비교 데이터 없음' : `${summary.deltaRate > 0 ? '+' : ''}${summary.deltaRate.toFixed(1)}% 변화`}</small>
            </article>
            <article className="visit-summary__item sales-summary__orders">
              <span>{year}년 누적 주문</span>
              <strong>{num(summary.totalOrders)}</strong>
              <small>최근 집계 {summary.latest?.month} · {num(summary.latest?.orderCount ?? 0)}건</small>
            </article>
            <article className="visit-summary__item order-summary__aov">
              <span>연간 평균 객단가</span>
              <strong>{num(summary.averageOrderValue)}</strong>
              <small>누적 매출 ÷ 누적 주문 건수</small>
            </article>
          </div>

          <section className="visit-trend sales-trend" aria-label="월별 매출 추이">
            <header className="visit-trend__head">
              <div>
                <span>TREND</span>
                <h2>{year}년 월별 매출 추이</h2>
                <p>집계된 월의 매출 변화를 시간순으로 표시합니다.</p>
              </div>
              {summary.peak && (
                <div className="visit-trend__peak sales-trend__peak">
                  <span>최고 매출월</span>
                  <strong>{num(summary.peak.sales)}</strong>
                  <small>{summary.peak.month}</small>
                </div>
              )}
            </header>
            <LineChart series={trendSeries} labels={trendLabels} height={220} />
          </section>
        </>
      )}

      <div className="visit-list-head">
        <div>
          <h2>{year}년 월별 매출</h2>
          <p>월 행에 마우스를 올리면 오른쪽에서 일별 주문·매출을 바로 확인할 수 있습니다.</p>
        </div>
        {!monthly.isLoading && !monthly.isError && <span>{num(summary.monthCount)}개월</span>}
      </div>

      {monthly.isLoading ? (
        <Spinner />
      ) : monthly.isError ? (
        <ErrorBox error={monthly.error} />
      ) : (monthly.data?.months.length ?? 0) === 0 ? (
        <EmptyBox />
      ) : (
        <div className="visit-table-wrap">
          <table className="table sales-table">
            <thead>
              <tr>
                <th aria-label="상세 열기" />
                <th>년-월</th>
                <th>주문</th>
                <th>매출</th>
                <th>객단가</th>
                <th>연 매출 비중</th>
              </tr>
            </thead>
            <tbody>
              {monthly.data!.months.map((month) => {
                const isOpen = openMonth === month.month;
                const share = summary.totalSales ? (month.sales / summary.totalSales) * 100 : 0;
                return (
                  <tr
                    key={month.month}
                    className={isOpen ? 'table__row--clickable visit-table__row visit-table__row--open' : 'table__row--clickable visit-table__row'}
                    onMouseEnter={() => previewMonth(month.month)}
                    onMouseLeave={schedulePreviewClose}
                    onClick={() => togglePinnedMonth(month.month)}
                  >
                    <td className="visit-table__toggle">
                      <button
                        type="button"
                        aria-label={`${month.month} 일별 상세 ${pinnedMonth === month.month ? '닫기' : '고정해서 열기'}`}
                        aria-expanded={isOpen}
                        onClick={(event) => {
                          event.stopPropagation();
                          togglePinnedMonth(month.month);
                        }}
                      >
                        <span aria-hidden="true">›</span>
                      </button>
                    </td>
                    <td className="visit-table__date">{month.month}</td>
                    <td className="td-right sales-table__orders">{num(month.orderCount)}</td>
                    <td className="td-right sales-table__sales">{num(month.sales)}</td>
                    <td className="td-right sales-table__aov">{num(month.orderCount ? Math.round(month.sales / month.orderCount) : 0)}</td>
                    <td>
                      <div className="sales-share" aria-label={`연 매출의 ${share.toFixed(1)}%`}>
                        <div><i style={{ width: `${Math.min(100, share)}%` }} /></div>
                        <small>{share.toFixed(1)}%</small>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openMonth && openMonthRow && (
        <aside
          className="visit-preview sales-preview"
          aria-label={`${openMonthRow.month} 일별 매출`}
          onMouseEnter={cancelHoverClose}
          onMouseLeave={schedulePreviewClose}
        >
          <section className="visit-detail">
            <header className="visit-detail__head">
              <div>
                <span>{year} MONTHLY DETAIL</span>
                <h3>{openMonthRow.month} 일별 매출</h3>
              </div>
              <div className="visit-detail__actions">
                <strong>주문 {num(openMonthRow.orderCount)} · 매출 {num(openMonthRow.sales)}</strong>
                <button
                  type="button"
                  aria-label="일별 매출 닫기"
                  onClick={() => {
                    setHoverMonth(null);
                    setPinnedMonth(null);
                  }}
                >
                  ×
                </button>
              </div>
            </header>
            {daily.isLoading ? (
              <Spinner />
            ) : daily.isError ? (
              <ErrorBox error={daily.error} />
            ) : (daily.data?.days.length ?? 0) === 0 ? (
              <EmptyBox />
            ) : (
              <div className="visit-detail__table-wrap">
                <table className="table table--nested sales-day-table">
                  <thead>
                    <tr>
                      <th>날짜</th>
                      <th>요일</th>
                      <th>주문</th>
                      <th>매출</th>
                      <th>객단가</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(daily.data?.days ?? []).map((day) => {
                      const weekday = weekdayOf(day.date);
                      return (
                        <tr key={day.date}>
                          <td><span className="visit-hour">{day.date}</span></td>
                          <td><span className={`visit-week-badge visit-week-badge--${weekday}`}>{WEEKDAYS[weekday]}</span></td>
                          <td className="td-right sales-day-table__orders">{num(day.orderCount)}</td>
                          <td className="td-right sales-day-table__sales">{num(day.sales)}</td>
                          <td className="td-right sales-day-table__aov">{num(day.orderCount ? Math.round(day.sales / day.orderCount) : 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </aside>
      )}
    </section>
  );
}
